/**
 * Product Card Enhanced - Quick Add & Interactions
 * Handles: Quick add to cart, image carousel, swatch selection
 */

(function() {
  'use strict';

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initQuickAdd();
    initImageCarousels();
    initSwatchSelection();
  }

  /**
   * Quick Add to Cart Functionality
   */
  function initQuickAdd() {
    document.addEventListener('click', function(e) {
      const quickAddBtn = e.target.closest('[data-quick-add-btn]');
      if (!quickAddBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = quickAddBtn.dataset.variantId;
      const hasVariants = quickAddBtn.dataset.hasVariants === 'true';
      const productHandle = quickAddBtn.dataset.productHandle;

      // If product has variants and we might need to show a selector
      // For now, we'll add the default variant
      // You can modify this to show a quick view modal for variant selection

      if (!variantId) {
        console.error('No variant ID found');
        return;
      }

      // Show loading state
      quickAddBtn.classList.add('is-loading');
      quickAddBtn.classList.remove('is-success');

      // Add to cart via Fetch API
      addToCart(variantId, 1)
        .then(response => {
          quickAddBtn.classList.remove('is-loading');
          quickAddBtn.classList.add('is-success');

          // Update cart count in header
          updateCartCount();

          // Open cart drawer if available
          openCartDrawer();

          // Reset success state after 2 seconds
          setTimeout(() => {
            quickAddBtn.classList.remove('is-success');
          }, 2000);
        })
        .catch(error => {
          console.error('Add to cart error:', error);
          quickAddBtn.classList.remove('is-loading');
          
          // Show error state briefly
          quickAddBtn.style.background = '#ef4444';
          setTimeout(() => {
            quickAddBtn.style.background = '';
          }, 1000);
        });
    });
  }

  /**
   * Add item to cart using Fetch API
   */
  function addToCart(variantId, quantity = 1) {
    const formData = {
      items: [{
        id: parseInt(variantId),
        quantity: quantity
      }]
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    });
  }

  /**
   * Update cart count in header
   */
  function updateCartCount() {
    fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(cart => {
      // Update all cart count elements
      const cartCountElements = document.querySelectorAll(
        '[data-cart-count], .cart-count, .cart-count-bubble, .header__cart-count, #cart-icon-bubble span'
      );
      
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
          el.style.display = '';
        }
      });

      // Dispatch custom event for other scripts to listen to
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: cart }
      }));
    })
    .catch(error => console.error('Error fetching cart:', error));
  }

  /**
   * Open cart drawer if theme has one
   */
  function openCartDrawer() {
    // Try common cart drawer selectors
    const cartDrawer = document.querySelector(
      '[data-cart-drawer], .cart-drawer, #cart-drawer, .drawer--cart, [data-drawer="cart"]'
    );
    
    if (cartDrawer) {
      // Try common methods to open drawer
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else if (cartDrawer.classList.contains('hidden')) {
        cartDrawer.classList.remove('hidden');
      } else {
        cartDrawer.classList.add('is-open', 'active', 'open');
        cartDrawer.setAttribute('aria-hidden', 'false');
      }

      // Also add body class for overlay
      document.body.classList.add('cart-drawer-open', 'drawer-open', 'overflow-hidden');
    }

    // Dispatch event for custom cart drawer implementations
    document.dispatchEvent(new CustomEvent('cart:open'));
    
    // Try to trigger Shopify theme cart drawer (Dawn theme style)
    const cartDrawerTrigger = document.querySelector('[data-cart-toggle], .cart-drawer-trigger');
    if (cartDrawerTrigger) {
      cartDrawerTrigger.click();
    }
  }

  /**
   * Image Carousel Functionality
   */
  function initImageCarousels() {
    const cards = document.querySelectorAll('.product-card-enhanced');
    
    cards.forEach(card => {
      const carousel = card.querySelector('.product-card-enhanced__carousel');
      const dots = card.querySelector('.product-card-enhanced__dots');
      
      if (!carousel || !dots) return;
      
      const images = carousel.querySelectorAll('.product-card-enhanced__image');
      const dotButtons = dots.querySelectorAll('.product-card-enhanced__dot');
      
      if (images.length <= 1) return;

      // Dot click handler
      dotButtons.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          showImage(images, dotButtons, index);
        });
      });

      // Optional: Auto-advance on hover
      let autoPlayInterval;
      
      card.addEventListener('mouseenter', () => {
        let currentIndex = 0;
        images.forEach((img, i) => {
          if (img.classList.contains('is-active')) currentIndex = i;
        });
        
        autoPlayInterval = setInterval(() => {
          currentIndex = (currentIndex + 1) % images.length;
          showImage(images, dotButtons, currentIndex);
        }, 1500);
      });

      card.addEventListener('mouseleave', () => {
        clearInterval(autoPlayInterval);
        // Reset to first image
        showImage(images, dotButtons, 0);
      });
    });
  }

  function showImage(images, dots, index) {
    images.forEach((img, i) => {
      img.classList.toggle('is-active', i === index);
    });
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  /**
   * Swatch Selection Functionality
   */
  function initSwatchSelection() {
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('.product-card-enhanced__swatch');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();

      const card = swatch.closest('.product-card-enhanced');
      if (!card) return;

      // Update active swatch
      const swatches = card.querySelectorAll('.product-card-enhanced__swatch');
      swatches.forEach(s => s.classList.remove('is-active'));
      swatch.classList.add('is-active');

      // Get variant data
      const variantId = swatch.dataset.variantId;
      const imageUrl = swatch.dataset.imageUrl;

      // Update quick add button variant
      const quickAddBtn = card.querySelector('[data-quick-add-btn]');
      if (quickAddBtn && variantId) {
        quickAddBtn.dataset.variantId = variantId;
      }

      // Update hidden form variant
      const variantInput = card.querySelector('[data-variant-input]');
      if (variantInput && variantId) {
        variantInput.value = variantId;
      }

      // Update main image if variant has specific image
      if (imageUrl) {
        const mainImage = card.querySelector('.product-card-enhanced__image.is-active');
        if (mainImage) {
          mainImage.src = imageUrl;
        }
      }
    });
  }

  // Re-initialize when Shopify section is reloaded (for theme editor)
  document.addEventListener('shopify:section:load', function(e) {
    init();
  });

})();
